import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { ModuleTips } from '../components/onboarding';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Car, Plus, Trash2, MapPin, Calendar, DollarSign, Loader2, Route, TrendingUp, Square, CheckSquare
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Mileage = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedMileage, setSelectedMileage] = useState([]);
  const [confirmDeleteMileageId, setConfirmDeleteMileageId] = useState(null);
  const [bulkDeleteMileageArmed, setBulkDeleteMileageArmed] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_location: '',
    end_location: '',
    miles: '',
    purpose: '',
    round_trip: false,
    notes: ''
  });

  useEffect(() => {
    fetchMileage();
  }, []);

  const fetchMileage = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mileage`, {
        withCredentials: true
      });
      setEntries(response.data.entries);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error(t('mileage.failed_to_load'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.start_location || !formData.end_location || !formData.miles || !formData.purpose) {
      toast.error(t('common.required'));
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${API_URL}/api/mileage`,
        {
          ...formData,
          miles: parseFloat(formData.miles)
        },
        { withCredentials: true }
      );
      toast.success(t('mileage.entry_added'));
      setShowForm(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        start_location: '',
        end_location: '',
        miles: '',
        purpose: '',
        round_trip: false,
        notes: ''
      });
      fetchMileage();
    } catch (error) {
      toast.error(t('mileage.failed_to_add'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (mileageId) => {
    if (confirmDeleteMileageId !== mileageId) {
      setConfirmDeleteMileageId(mileageId);
      toast.info('Click delete again to confirm', { duration: 3000 });
      setTimeout(() => setConfirmDeleteMileageId(null), 3000);
      return;
    }
    setConfirmDeleteMileageId(null);
    try {
      await axios.delete(`${API_URL}/api/mileage/${mileageId}`, {
        withCredentials: true
      });
      toast.success(t('mileage.entry_deleted'));
      fetchMileage();
    } catch (error) {
      toast.error(t('mileage.failed_to_delete'));
    }
  };

  const toggleMileageSelect = (id) => {
    setSelectedMileage(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const handleSelectAllMileage = () => {
    setSelectedMileage(prev => prev.length === entries.length ? [] : entries.map(e => e.mileage_id));
  };
  const handleBulkDeleteMileage = async () => {
    if (selectedMileage.length === 0) return;
    if (!bulkDeleteMileageArmed) {
      setBulkDeleteMileageArmed(true);
      toast.info(`Click again to delete ${selectedMileage.length} trips`, { duration: 3000 });
      setTimeout(() => setBulkDeleteMileageArmed(false), 3000);
      return;
    }
    setBulkDeleteMileageArmed(false);
    try {
      // D2: Use withCredentials (httpOnly cookie) — no localStorage token
      const response = await axios.post(`${API_URL}/api/mileage/bulk-delete`, selectedMileage,
        { withCredentials: true });
      toast.success(t('toast.deleted_docs', { count: response.data.count }));
      setSelectedMileage([]);
      fetchMileage();
    } catch (error) { toast.error(t('toast.failed_to_delete')); }
  };

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="mileage-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-heading">{t('mileage.title')}</h1>
            <p className="text-stone-600">{t('mileage.irs_rate_info', { rate: summary?.irs_rate || '0.70', year: summary?.year || new Date().getFullYear() })}</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            {t('mileage.log_trip')}
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Route className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">{t('mileage.total_miles')}</p>
                    <p className="text-2xl font-bold text-stone-900 font-heading">{summary.total_miles.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">{t('mileage.total_deduction')}</p>
                    <p className="text-2xl font-bold text-emerald-600 font-heading">${Number(summary.total_deduction || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Car className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">{t('mileage.total_trips')}</p>
                    <p className="text-2xl font-bold text-stone-900 font-heading">{summary.total_entries ?? summary.trip_count ?? 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">{t('mileage.irs_rate')}</p>
                    <p className="text-2xl font-bold text-stone-900 font-heading">${Number(summary.irs_rate || 0).toFixed(2)}{t('mileage.per_mile')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Entry Form */}
        {showForm && (
          <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
            <CardHeader>
              <CardTitle className="font-heading">{t('mileage.log_new_trip')}</CardTitle>
              <CardDescription>{t('mileage.record_business_trip')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('mileage.date')}</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="mileage-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('mileage.miles_one_way')}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="15.5"
                    value={formData.miles}
                    onChange={(e) => setFormData({ ...formData, miles: e.target.value })}
                    data-testid="mileage-miles-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('mileage.start_location')}</Label>
                  <Input
                    placeholder="Home Office"
                    value={formData.start_location}
                    onChange={(e) => setFormData({ ...formData, start_location: e.target.value })}
                    data-testid="mileage-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('mileage.end_location')}</Label>
                  <Input
                    placeholder="Client Office Downtown"
                    value={formData.end_location}
                    onChange={(e) => setFormData({ ...formData, end_location: e.target.value })}
                    data-testid="mileage-end-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('mileage.business_purpose')}</Label>
                <Input
                  placeholder={t('mileage.purpose_placeholder')}
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  data-testid="mileage-purpose-input"
                />
              </div>
              <div className="flex items-center gap-4">
                <Button
                  variant={formData.round_trip ? 'default' : 'outline'}
                  size="sm"
                  className={formData.round_trip ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  onClick={() => setFormData({ ...formData, round_trip: !formData.round_trip })}
                  data-testid="mileage-round-trip-btn"
                >
                  {formData.round_trip ? `✓ ${t('mileage.round_trip')}` : t('mileage.one_way_trip')}
                </Button>
              </div>
              <div className="space-y-2">
                <Label>{t('mileage.notes_optional')}</Label>
                <Input
                  placeholder={t('mileage.additional_details')}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="mileage-notes-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mileage-add-btn">
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  {t('mileage.add_trip')}
                </Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mileage Entries List */}
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading">{t('mileage.trip_log')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-stone-400" />
              </div>
            ) : entries.length === 0 ? (
              <div className="py-12 text-center">
                <Car className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500">{t('mileage.no_entries')}</p>
                <p className="text-sm text-stone-400">{t('mileage.start_logging')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={handleSelectAllMileage} data-testid="select-all-mileage">
                    {selectedMileage.length === entries.length && entries.length > 0 ? <><CheckSquare className="w-4 h-4 mr-1" /> Deselect All</> : <><Square className="w-4 h-4 mr-1" /> Select All</>}
                  </Button>
                  {selectedMileage.length > 0 && (
                    <Button variant="destructive" size="sm" onClick={handleBulkDeleteMileage} data-testid="bulk-delete-mileage">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedMileage.length} Selected
                    </Button>
                  )}
                </div>
                {entries.map((entry) => (
                  <div key={entry.mileage_id} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-stone-50 rounded-xl border gap-3 ${selectedMileage.includes(entry.mileage_id) ? 'border-primary ring-1 ring-primary/20' : 'border-stone-100'}`} data-testid={`mileage-entry-${entry.mileage_id}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox checked={selectedMileage.includes(entry.mileage_id)} onCheckedChange={() => toggleMileageSelect(entry.mileage_id)} className="mt-1" />
                      <div className="w-10 h-10 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                        <Car className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-stone-900 truncate">{entry.start_location}</span>
                          <span className="text-stone-400 text-xs">→</span>
                          <span className="font-medium text-sm text-stone-900 truncate">{entry.end_location}</span>
                          {entry.round_trip && <Badge variant="secondary" className="text-xs">{t('mileage.round_trip')}</Badge>}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs sm:text-sm text-stone-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(entry.date).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{entry.purpose}</span>
                          </span>
                        </div>
                        {/* Mobile: Show miles and deduction below */}
                        <div className="flex items-center gap-4 mt-2 sm:hidden">
                          <p className="font-semibold text-stone-900 text-sm">{entry.miles} miles</p>
                          <p className="text-sm text-emerald-600">${entry.deduction_amount.toFixed(2)} {t('mileage.deduction')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-4">
                      {/* Desktop: Show miles and deduction */}
                      <div className="hidden sm:block text-right">
                        <p className="font-semibold text-stone-900">{entry.miles} miles</p>
                        <p className="text-sm text-emerald-600">${entry.deduction_amount.toFixed(2)} {t('mileage.deduction')}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(entry.mileage_id)}
                        data-testid={`mileage-delete-${entry.mileage_id}`}
                        className="h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="mileage" />
  </>
  );
};

export default Mileage;
